import { EventSource } from "eventsource";
import { useAuthStore } from "@/lib/store/auth-enhanced";

/**
 * AI Agent REST API Client
 *
 * Communicates with the backend NestJS AI agent endpoints
 */

import type {
  AiConversationThread,
  AiThreadListResponse,
  ClientToolName,
  ThreadFilters,
  UpdateThreadRequest,
} from "@salon/types/ai-agent";

/**
 * Client tool reference - just the tool name
 * Backend will look up the full definition from shared registry
 */
export interface ClientToolReference {
  name: ClientToolName;
}

export interface SendMessageRequest {
  message: string;
  threadId?: string;
  clientTools?: ClientToolReference[];
  attachmentIds?: string[];
  viewContext?: string;
}

export interface AttachmentDto {
  id: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  fileType: "PDF" | "IMAGE" | "VIDEO" | "AUDIO" | "DOCUMENT";
  url: string;
  createdAt: string;
}

export interface ResumeConversationRequest {
  threadId: string;
  decision: "approve" | "reject" | "edit";
  message?: string;
}

export interface AgentResponse {
  threadId: string;
  message: string;
  interrupted?: boolean;
  interruptInfo?: {
    type: string;
    description: string;
    decisions: string[];
  };
}

export interface TextEvent {
  id: string;
  agent: string;
  content: string;
  attachments?: Array<{ type: string; url: string }>;
}

export interface TextDeltaEvent {
  id: string;
  agent?: string;
  delta: string;
  done?: boolean;
}

export interface ToolCallEvent {
  id: string;
  agent: string;
  tool: string;
  args: Record<string, any>;
}

export interface ToolResultEvent {
  id: string;
  agent?: string;
  result: string;
}

export interface InterruptEvent {
  type: string;
  description: string;
  decisions: string[];
  actionRequests?: Array<{
    name: string;
    args: Record<string, any>;
    description?: string;
  }>;
}

export interface ClientToolExecutionEvent {
  type: "client_tool_execution";
  toolName: string;
  args: Record<string, any>;
  description?: string;
}

export interface SuggestionsEvent {
  suggestions: string[];
}

export interface DoneEvent {
  threadId: string;
}

export interface ErrorEvent {
  message: string;
  code?: string;
}

export type StreamEvent =
  | { type: "text"; data: TextEvent }
  | { type: "text_delta"; data: TextDeltaEvent }
  | { type: "tool_call"; data: ToolCallEvent }
  | { type: "tool_result"; data: ToolResultEvent }
  | { type: "interrupt"; data: InterruptEvent }
  | { type: "client_tool_execution"; data: ClientToolExecutionEvent }
  | { type: "suggestions"; data: SuggestionsEvent }
  | { type: "done"; data: DoneEvent }
  | { type: "error"; data: ErrorEvent };

export class AIAgentClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private getAuthToken(): string | null {
    return useAuthStore.getState().accessToken;
  }

  /**
   * Send a message to the AI agent
   */
  async sendMessage(salonId: string, request: SendMessageRequest): Promise<AgentResponse> {
    const token = this.getAuthToken();
    if (!token) {
      throw new Error("Not authenticated");
    }

    const response = await fetch(`${this.baseUrl}/ai-agent/${salonId}/message`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to send message: ${error}`);
    }

    return response.json();
  }

  /**
   * Send a message to the AI agent with SSE streaming
   * Returns a callback function to cleanup the EventSource connection
   */
  streamMessage(
    salonId: string,
    request: SendMessageRequest,
    onEvent: (event: StreamEvent) => void
  ): () => void {
    const token = this.getAuthToken();
    if (!token) {
      throw new Error("Not authenticated");
    }

    // Build query string from request
    const params = new URLSearchParams({
      message: request.message,
      ...(request.threadId && { threadId: request.threadId }),
      // Serialize clientTools as JSON string for GET request
      ...(request.clientTools &&
        request.clientTools.length > 0 && {
          clientTools: JSON.stringify(request.clientTools),
        }),
      // Serialize attachmentIds as JSON string for GET request
      ...(request.attachmentIds &&
        request.attachmentIds.length > 0 && {
          attachmentIds: JSON.stringify(request.attachmentIds),
        }),
      // Include viewContext if provided
      ...(request.viewContext && { viewContext: request.viewContext }),
    });

    // Create EventSource with Authorization header using fetch option
    const eventSource = new EventSource(`${this.baseUrl}/ai-agent/${salonId}/stream?${params}`, {
      fetch: (input, init) =>
        fetch(input, {
          ...init,
          headers: {
            ...init?.headers,
            Authorization: `Bearer ${token}`,
          },
        }),
    });

    // Handle text events (AI responses)
    eventSource.addEventListener("text", (event) => {
      try {
        const data = JSON.parse(event.data);
        onEvent({ type: "text", data });
      } catch (error) {
        console.error("Failed to parse text event:", error);
      }
    });

    // Handle text delta events (streaming tokens)
    eventSource.addEventListener("text_delta", (event) => {
      try {
        const data = JSON.parse(event.data);
        onEvent({ type: "text_delta", data });
      } catch (error) {
        console.error("Failed to parse text_delta event:", error);
      }
    });

    // Handle tool call events
    eventSource.addEventListener("tool_call", (event) => {
      try {
        const data = JSON.parse(event.data);
        onEvent({ type: "tool_call", data });
      } catch (error) {
        console.error("Failed to parse tool_call event:", error);
      }
    });

    // Handle tool result events
    eventSource.addEventListener("tool_result", (event) => {
      try {
        const data = JSON.parse(event.data);
        onEvent({ type: "tool_result", data });
      } catch (error) {
        console.error("Failed to parse tool_result event:", error);
      }
    });

    // Handle interrupt events (HITL)
    eventSource.addEventListener("interrupt", (event) => {
      try {
        const data = JSON.parse(event.data);
        // Check if this is a client tool execution interrupt
        if (data.type === "client_tool_execution") {
          onEvent({ type: "client_tool_execution", data });
        } else {
          onEvent({ type: "interrupt", data });
        }
      } catch (error) {
        console.error("Failed to parse interrupt event:", error);
      }
    });

    // Handle done events
    eventSource.addEventListener("done", (event) => {
      try {
        const data = JSON.parse(event.data);
        onEvent({ type: "done", data });
        eventSource.close();
      } catch (error) {
        console.error("Failed to parse done event:", error);
      }
    });

    // Handle error events
    eventSource.addEventListener("error", (event) => {
      // Check if this is a proper SSE error event with data
      if ((event as any).data) {
        try {
          const data = JSON.parse((event as any).data);
          onEvent({ type: "error", data });
        } catch (error) {
          console.error("Failed to parse error event:", error);
          onEvent({
            type: "error",
            data: {
              message: "Failed to parse error event",
            },
          });
        }
      } else {
        // Network-level error (connection closed, timeout, etc.)
        // Emit error so UI can show feedback and stop "thinking" state
        console.error("EventSource connection closed (network error)");
        onEvent({
          type: "error",
          data: {
            message: "Connection lost. Please try again.",
            code: "STREAM_CONNECTION_ERROR",
          },
        });
      }
      eventSource.close();
    });

    // Return cleanup function
    return () => {
      eventSource.close();
    };
  }

  /**
   * Resume an interrupted conversation with a decision
   */
  async resumeConversation(
    salonId: string,
    request: ResumeConversationRequest
  ): Promise<AgentResponse> {
    const token = this.getAuthToken();
    if (!token) {
      throw new Error("Not authenticated");
    }

    const response = await fetch(`${this.baseUrl}/ai-agent/${salonId}/resume`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to resume conversation: ${error}`);
    }

    return response.json();
  }

  /**
   * Resume conversation with SSE streaming
   * Can resume after:
   * 1. Client tool execution (pass toolResult)
   * 2. Interrupt decision (pass decision)
   */
  resume(
    salonId: string,
    threadId: string,
    input:
      | { type: "toolResult"; toolName: string; result: any }
      | { type: "decision"; decision: "approve" | "reject" | "edit" },
    clientTools: ClientToolReference[],
    onEvent: (event: StreamEvent) => void
  ): () => void {
    const token = this.getAuthToken();
    if (!token) {
      throw new Error("Not authenticated");
    }

    // Build query string based on input type
    const params = new URLSearchParams({ threadId });

    if (input.type === "toolResult") {
      // Resume with client tool result
      params.set(
        "toolResult",
        JSON.stringify({
          toolName: input.toolName,
          result: input.result,
        })
      );
    } else if (input.type === "decision") {
      // Resume with interrupt decision
      params.set("decision", input.decision);
    }

    if (clientTools && clientTools.length > 0) {
      params.set("clientTools", JSON.stringify(clientTools));
    }

    // Use EventSource with GET request (SSE standard)
    const eventSource = new EventSource(
      `${this.baseUrl}/ai-agent/${salonId}/resume-stream?${params}`,
      {
        fetch: (input, init) =>
          fetch(input, {
            ...init,
            headers: {
              ...init?.headers,
              Authorization: `Bearer ${token}`,
            },
          }),
      }
    );

    // Setup event listeners for all event types
    const eventTypes = [
      "text",
      "text_delta",
      "tool_call",
      "tool_result",
      "interrupt",
      "client_tool_execution",
      "suggestions",
      "error",
    ];
    eventTypes.forEach((eventType) => {
      eventSource.addEventListener(eventType, (event) => {
        try {
          const data = JSON.parse(event.data);
          onEvent({ type: eventType, data } as StreamEvent);
        } catch (error) {
          console.error(`Failed to parse ${eventType} event:`, error);
        }
      });
    });

    // Handle 'done' event separately to close the connection
    eventSource.addEventListener("done", (event) => {
      try {
        const data = JSON.parse(event.data);
        onEvent({ type: "done", data } as StreamEvent);
        // Close the connection after done event
        eventSource.close();
      } catch (error) {
        console.error("Failed to parse done event:", error);
      }
    });

    // Handle connection errors
    eventSource.onerror = (error) => {
      // Network-level error (connection closed, timeout, etc.)
      // Emit error so UI can show feedback and stop "thinking" state
      console.error("EventSource connection closed (network error):", error);
      onEvent({
        type: "error",
        data: {
          message: "Connection lost. Please try again.",
          code: "STREAM_CONNECTION_ERROR",
        },
      });
      eventSource.close();
    };

    // Return cleanup function
    return () => {
      eventSource.close();
    };
  }

  /**
   * List conversation threads
   */
  async listThreads(salonId: string, filters?: ThreadFilters): Promise<AiThreadListResponse> {
    const token = this.getAuthToken();
    if (!token) {
      throw new Error("Not authenticated");
    }

    const params = new URLSearchParams();
    if (filters?.status) params.append("status", filters.status);
    if (filters?.search) params.append("search", filters.search);
    if (filters?.limit) params.append("limit", filters.limit.toString());
    if (filters?.offset) params.append("offset", filters.offset.toString());

    const response = await fetch(`${this.baseUrl}/ai-agent/${salonId}/threads?${params}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to list threads: ${error}`);
    }

    return response.json();
  }

  /**
   * Get a single thread by ID
   */
  async getThread(salonId: string, threadId: string): Promise<AiConversationThread> {
    const token = this.getAuthToken();
    if (!token) {
      throw new Error("Not authenticated");
    }

    const response = await fetch(`${this.baseUrl}/ai-agent/${salonId}/threads/${threadId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to get thread: ${error}`);
    }

    return response.json();
  }

  /**
   * Stream thread conversation history as SSE events
   * Replays the conversation using the same event format as live streaming
   * Returns a cleanup function to close the EventSource connection
   */
  streamThreadHistory(
    salonId: string,
    threadId: string,
    onEvent: (event: StreamEvent) => void
  ): () => void {
    const token = this.getAuthToken();
    if (!token) {
      throw new Error("Not authenticated");
    }

    // Create EventSource with Authorization header
    const eventSource = new EventSource(
      `${this.baseUrl}/ai-agent/${salonId}/threads/${threadId}/history`,
      {
        fetch: (input, init) =>
          fetch(input, {
            ...init,
            headers: {
              ...init?.headers,
              Authorization: `Bearer ${token}`,
            },
          }),
      }
    );

    // Setup event listeners for all event types (same as streamMessage)
    const eventTypes = [
      "text",
      "text_delta",
      "tool_call",
      "tool_result",
      "client_tool_execution",
      "error",
    ];
    eventTypes.forEach((eventType) => {
      eventSource.addEventListener(eventType, (event) => {
        try {
          const data = JSON.parse(event.data);
          onEvent({ type: eventType, data } as StreamEvent);
        } catch (error) {
          console.error(`Failed to parse ${eventType} event:`, error);
        }
      });
    });

    // Handle interrupt events (HITL) - parse to check if it's client tool execution
    eventSource.addEventListener("interrupt", (event) => {
      try {
        const data = JSON.parse(event.data);
        // Check if this is a client tool execution interrupt
        if (data.type === "client_tool_execution") {
          onEvent({ type: "client_tool_execution", data });
        } else {
          onEvent({ type: "interrupt", data });
        }
      } catch (error) {
        console.error("Failed to parse interrupt event:", error);
      }
    });

    // Handle 'done' event to close the connection
    eventSource.addEventListener("done", (event) => {
      try {
        const data = JSON.parse(event.data);
        onEvent({ type: "done", data } as StreamEvent);
        eventSource.close();
      } catch (error) {
        console.error("Failed to parse done event:", error);
      }
    });

    // Handle connection errors
    eventSource.onerror = (error) => {
      // Network-level error (connection closed, timeout, etc.)
      // Emit error so UI can show feedback and stop "thinking" state
      console.error("EventSource connection closed (network error):", error);
      onEvent({
        type: "error",
        data: {
          message: "Connection lost. Please try again.",
          code: "STREAM_CONNECTION_ERROR",
        },
      });
      eventSource.close();
    };

    // Return cleanup function
    return () => {
      eventSource.close();
    };
  }

  /**
   * Update thread title
   */
  async updateThreadTitle(
    salonId: string,
    threadId: string,
    title: string
  ): Promise<AiConversationThread> {
    const token = this.getAuthToken();
    if (!token) {
      throw new Error("Not authenticated");
    }

    const response = await fetch(`${this.baseUrl}/ai-agent/${salonId}/threads/${threadId}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ title } as UpdateThreadRequest),
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to update thread title: ${error}`);
    }

    return response.json();
  }

  /**
   * Archive a thread
   */
  async archiveThread(salonId: string, threadId: string): Promise<AiConversationThread> {
    const token = this.getAuthToken();
    if (!token) {
      throw new Error("Not authenticated");
    }

    const response = await fetch(
      `${this.baseUrl}/ai-agent/${salonId}/threads/${threadId}/archive`,
      {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to archive thread: ${error}`);
    }

    return response.json();
  }

  /**
   * Delete a thread (soft delete)
   */
  async deleteThread(salonId: string, threadId: string): Promise<{ success: boolean }> {
    const token = this.getAuthToken();
    if (!token) {
      throw new Error("Not authenticated");
    }

    const response = await fetch(`${this.baseUrl}/ai-agent/${salonId}/threads/${threadId}`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to delete thread: ${error}`);
    }

    return response.json();
  }

  /**
   * Upload an attachment for AI chat
   * Just uploads the file and returns the asset ID
   */
  async uploadAttachment(salonId: string, file: File): Promise<AttachmentDto> {
    const token = this.getAuthToken();
    if (!token) {
      throw new Error("Not authenticated");
    }

    const formData = new FormData();
    formData.append("file", file);

    const response = await fetch(`${this.baseUrl}/ai-agent/${salonId}/attachments`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to upload attachment: ${error}`);
    }

    return response.json();
  }

  /**
   * Get attachments by message ID
   */
  async getAttachmentsByMessageId(salonId: string, messageId: string): Promise<AttachmentDto[]> {
    const token = this.getAuthToken();
    if (!token) {
      throw new Error("Not authenticated");
    }

    const response = await fetch(
      `${this.baseUrl}/ai-agent/${salonId}/attachments?messageId=${messageId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to get attachments: ${error}`);
    }

    return response.json();
  }

  /**
   * Get attachments by IDs
   */
  async getAttachmentsByIds(salonId: string, attachmentIds: string[]): Promise<AttachmentDto[]> {
    const token = this.getAuthToken();
    if (!token) {
      throw new Error("Not authenticated");
    }

    const response = await fetch(
      `${this.baseUrl}/ai-agent/${salonId}/attachments?attachmentIds=${attachmentIds.join(",")}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to get attachments: ${error}`);
    }

    return response.json();
  }

  /**
   * Delete an uploaded asset (before it's linked to a message)
   */
  async deleteAsset(salonId: string, assetId: string): Promise<void> {
    const token = this.getAuthToken();
    if (!token) {
      throw new Error("Not authenticated");
    }

    const response = await fetch(`${this.baseUrl}/ai-agent/${salonId}/assets/${assetId}`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to delete asset: ${error}`);
    }
  }

  /**
   * Delete an attachment (after it's linked to a message)
   */
  async deleteAttachment(salonId: string, attachmentId: string): Promise<void> {
    const token = this.getAuthToken();
    if (!token) {
      throw new Error("Not authenticated");
    }

    const response = await fetch(
      `${this.baseUrl}/ai-agent/${salonId}/attachments/${attachmentId}`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to delete attachment: ${error}`);
    }
  }

  /**
   * Transcribe audio to text using Whisper API (via backend)
   * Currently free - no credit deduction
   */
  async transcribeAudio(salonId: string, audioFile: Blob, language?: string): Promise<string> {
    const token = this.getAuthToken();
    if (!token) {
      throw new Error("Not authenticated");
    }

    // Create form data
    const formData = new FormData();
    formData.append("file", audioFile, "audio.webm");

    // Build URL with optional language parameter
    const url = new URL(`${this.baseUrl}/ai-agent/${salonId}/audio/transcribe`);
    if (language) {
      url.searchParams.set("language", language);
    }

    const response = await fetch(url.toString(), {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to transcribe audio: ${error}`);
    }

    const result = await response.json();
    return result.text;
  }

  /**
   * Synthesize speech from text using OpenAI TTS API (via backend)
   * Deducts credits from salon balance
   */
  async synthesizeSpeech(salonId: string, text: string, voice?: string): Promise<Blob> {
    const token = this.getAuthToken();
    if (!token) {
      throw new Error("Not authenticated");
    }

    const response = await fetch(`${this.baseUrl}/ai-agent/${salonId}/audio/synthesize`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ text, voice }),
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to synthesize speech: ${error}`);
    }

    // Return audio blob
    return await response.blob();
  }

  /**
   * Fetch contextual suggestions for a thread
   * Returns empty array on error (silent failure)
   */
  async fetchSuggestions(salonId: string, threadId: string): Promise<string[]> {
    const token = this.getAuthToken();
    if (!token) {
      console.warn("[AIAgentClient] Not authenticated, cannot fetch suggestions");
      return [];
    }

    try {
      const response = await fetch(`${this.baseUrl}/ai-agent/${salonId}/suggestions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ threadId }),
      });

      if (!response.ok) {
        console.warn(`[AIAgentClient] Failed to fetch suggestions: ${response.statusText}`);
        return [];
      }

      const result = await response.json();
      return result.suggestions || [];
    } catch (error) {
      console.warn("[AIAgentClient] Error fetching suggestions:", error);
      return [];
    }
  }
}

// Create singleton instance
// Use dedicated AI Agent URL (separate service on port 3203)
const AI_AGENT_URL = import.meta.env.VITE_AI_AGENT_URL || "http://localhost:3203/api";

export const aiAgentClient = new AIAgentClient(AI_AGENT_URL);
